import { useState, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Paperclip, X, Loader2 } from "lucide-react";
import { careersAPI } from "../../services/api";

const INPUT_STYLE = {
  backgroundColor: "var(--lm-s1)",
  border: "1px solid var(--lm-border)",
  color: "var(--lm-ink)",
};

export default function CareersApplyForm({ postingId, source = "direct", onSuccess, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "",
    phone: "", cover_letter: "", gdpr_consent: false,
  });
  const [cvFile, setCvFile] = useState(null);   // { name, file_id }
  const [cvUploading, setCvUploading] = useState(false);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const fileInputRef = useRef(null);

  const { data: posting } = useQuery({
    queryKey: ["public-posting", postingId],
    queryFn: () => careersAPI.getPosting(postingId).then((r) => r.data),
    enabled: !!postingId,
    staleTime: 60000,
  });
  const questions = (posting?.questions ?? []).filter(
    (q) => q.question_type !== "select" || (q.options || []).length > 0
  );

  const handleCvChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvUploading(true);
    setError(null);
    try {
      const res = await careersAPI.uploadCV(file);
      setCvFile({ name: file.name, file_id: res.data.file_id });
    } catch (err) {
      setError(err?.response?.data?.detail || t("careers.form.uploadError"));
    } finally {
      setCvUploading(false);
    }
  };

  const applyMut = useMutation({
    mutationFn: (data) => careersAPI.apply(data),
    onSuccess: (res) => {
      onSuccess(res.data.tracking_token || "");
    },
    onError: (err) => {
      setError(err?.response?.data?.detail || t("careers.form.submitError"));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!form.gdpr_consent) {
      setError(t("careers.form.gdprRequired"));
      return;
    }
    const missing = questions.filter((q) => q.required && !answers[q.id]?.toString().trim());
    if (missing.length > 0) {
      setError(t("careers.form.questionsRequired"));
      return;
    }
    applyMut.mutate({
      ...form,
      job_posting_id: postingId,
      source,
      cv_file_id: cvFile?.file_id || null,
      custom_answers: questions.length > 0 ? answers : null,
    });
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const isExpired = posting?.expires_at && new Date(posting.expires_at) < new Date();
  if (isExpired) {
    return (
      <div
        className="px-5 py-6 rounded-xl text-center space-y-2"
        style={{ backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.20)" }}
      >
        <p className="text-sm font-medium" style={{ color: "#dc2626" }}>{t("careers.form.expiredTitle")}</p>
        <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
          {t("careers.form.expiredText")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div
          className="px-4 py-3 rounded-lg text-sm"
          style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.20)", color: "#dc2626" }}
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("careers.form.firstName")} required>
          <input
            required value={form.first_name} onChange={set("first_name")}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={INPUT_STYLE}
            placeholder={t("careers.form.firstNamePlaceholder")}
          />
        </Field>
        <Field label={t("careers.form.lastName")} required>
          <input
            required value={form.last_name} onChange={set("last_name")}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={INPUT_STYLE}
            placeholder={t("careers.form.lastNamePlaceholder")}
          />
        </Field>
        <Field label={t("careers.form.email")} required>
          <input
            required type="email" value={form.email} onChange={set("email")}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={INPUT_STYLE}
            placeholder={t("careers.form.emailPlaceholder")}
          />
        </Field>
        <Field label={t("careers.form.phone")}>
          <input
            type="tel" value={form.phone} onChange={set("phone")}
            className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
            style={INPUT_STYLE}
            placeholder="+216 XX XXX XXX"
          />
        </Field>
      </div>

      <Field label={t("careers.form.coverLetter")}>
        <textarea
          rows={5} value={form.cover_letter} onChange={set("cover_letter")}
          className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
          style={INPUT_STYLE}
          placeholder={t("careers.form.coverLetterPlaceholder")}
        />
      </Field>

      {/* Custom questions */}
      {questions.length > 0 && (
        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>
            {t("careers.form.additionalQuestions")}
          </p>
          {questions
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((q) => (
              <Field key={q.id} label={`${q.label}${q.required ? " *" : ""}`}>
                {q.question_type === "textarea" ? (
                  <textarea
                    rows={3}
                    required={q.required}
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
                    style={INPUT_STYLE}
                  />
                ) : q.question_type === "select" ? (
                  <select
                    required={q.required}
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={INPUT_STYLE}
                  >
                    <option value="">{t("careers.form.choose")}</option>
                    {(q.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : q.question_type === "boolean" ? (
                  <div className="flex gap-4">
                    {[["Oui", t("careers.form.yes")], ["Non", t("careers.form.no")]].map(([opt, lbl]) => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`q_${q.id}`}
                          value={opt}
                          checked={answers[q.id] === opt}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                          style={{ accentColor: "#5e6ad2" }}
                        />
                        <span className="text-sm" style={{ color: "var(--lm-ink)" }}>{lbl}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    type="text"
                    required={q.required}
                    value={answers[q.id] || ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={INPUT_STYLE}
                  />
                )}
              </Field>
            ))}
        </div>
      )}

      {/* CV Upload */}
      <Field label={t("careers.form.cvLabel")}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleCvChange}
          className="hidden"
        />
        {!cvFile ? (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={cvUploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm transition-all border-2 border-dashed"
            style={{
              backgroundColor: "transparent",
              borderColor: "var(--lm-border)",
              color: "var(--lm-ink-3)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5e6ad2"; e.currentTarget.style.color = "#5e6ad2"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--lm-border)"; e.currentTarget.style.color = "var(--lm-ink-3)"; }}
          >
            {cvUploading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t("careers.form.uploading")}</>
            ) : (
              <><Paperclip className="h-4 w-4" /> {t("careers.form.attachCv")}</>
            )}
          </button>
        ) : (
          <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg"
            style={{ backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.25)" }}
          >
            <Paperclip className="h-4 w-4 flex-shrink-0" style={{ color: "#5e6ad2" }} />
            <span className="text-sm flex-1 truncate" style={{ color: "var(--lm-ink)" }}>{cvFile.name}</span>
            <button
              type="button"
              onClick={() => { setCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              style={{ color: "var(--lm-ink-3)" }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </Field>

      {/* GDPR */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={form.gdpr_consent}
          onChange={(e) => setForm((f) => ({ ...f, gdpr_consent: e.target.checked }))}
          className="mt-0.5 flex-shrink-0"
          style={{ accentColor: "#5e6ad2" }}
        />
        <span className="text-xs leading-relaxed" style={{ color: "var(--lm-ink-2)" }}>
          {t("careers.form.gdprPre")}
          <a href="/privacy" target="_blank" style={{ color: "#5e6ad2" }}>{t("careers.form.gdprLink")}</a>
          {t("careers.form.gdprPost")}
        </span>
      </label>

      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button" onClick={onCancel}
            className="px-4 py-2.5 rounded-lg text-sm transition-all"
            style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}
          >
            {t("common.cancel")}
          </button>
        )}
        <button
          type="submit"
          disabled={applyMut.isPending}
          className="flex-1 sm:flex-none px-6 py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-60"
          style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}
        >
          {applyMut.isPending ? t("careers.form.submitting") : t("careers.form.submit")}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{label}</label>
      {children}
    </div>
  );
}
